import { FormLabel } from '@cmg/common';
import { Box, FormControl, MenuItem, Select, Stack, Typography } from '@cmg/design-system';
import type { FormikHelpers } from 'formik';
import { Field } from 'formik';
import React from 'react';

import type { Firm } from '../../../../../types/domain/firm/Firm';

type EntityTypeSectionProps = {
  linkTargetFirm: Firm | null;
  linkTargetDisplayName: string | null;
  metadata: {
    entityTypes: { value: string; label: string }[];
    industryTypes: { value: string; label: string }[];
  };
  metadataLoading: boolean;
  setFieldValue: FormikHelpers<unknown>['setFieldValue'];
};

export const LinkOrganizationEntityTypeSection: React.FC<EntityTypeSectionProps> = ({
  linkTargetFirm,
  linkTargetDisplayName,
  metadata,
  metadataLoading,
  setFieldValue,
}) => {
  if (!linkTargetDisplayName || linkTargetFirm?.details.entityType) {
    return null;
  }

  return (
    <Stack mt={1}>
      <Typography>
        Linking these organizations requires an Entity Type to be added to {linkTargetDisplayName}.
        Consider adding an Industry Type (optional) as well.
      </Typography>

      <Stack mt={1} spacing={2}>
        <Box flex={1}>
          <FormLabel required>Entity Type</FormLabel>
          <FormControl fullWidth disabled={metadataLoading}>
            <Field name="entityType">
              {({ field }) => (
                <Select
                  {...field}
                  value={field.value || ''}
                  onChange={event => {
                    void setFieldValue('entityType', event.target.value);
                  }}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 110,
                      },
                    },
                  }}
                >
                  {metadata.entityTypes.map(entityType => (
                    <MenuItem key={entityType.value} value={entityType.value}>
                      {entityType.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Field>
          </FormControl>
        </Box>

        {!linkTargetFirm?.details.industryType && (
          <Box flex={1}>
            <FormLabel>Industry Type</FormLabel>
            <FormControl fullWidth disabled={metadataLoading}>
              <Field name="industryType">
                {({ field }) => (
                  <Select
                    {...field}
                    value={field.value || ''}
                    onChange={event => {
                      void setFieldValue('industryType', event.target.value);
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 110,
                        },
                      },
                    }}
                  >
                    {metadata.industryTypes.map(industryType => (
                      <MenuItem key={industryType.value} value={industryType.value}>
                        {industryType.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Field>
            </FormControl>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
